import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

export interface PickerLens {
  id: string;
  display_name_fr: string;
  display_name_en: string;
  price_ttc: number;
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  lang: "fr" | "en" | string;
  currency: string;
  framePriceTtc: number;
  /** Options déjà chargées ; sinon chargées depuis le catalogue actif du pays. */
  options?: PickerLens[];
  countryId?: string;
  initialLensId?: string | null;
  confirmLabel?: string;
  onConfirm: (lens: PickerLens) => void;
}

/** Choix du package de verres avant paiement (page produit, panier, checkout). */
export function LensPickerSheet({ open, onOpenChange, lang, currency, framePriceTtc, options, countryId, initialLensId, confirmLabel, onConfirm }: Props) {
  const [loaded, setLoaded] = useState<PickerLens[] | null>(options ?? null);
  const [sel, setSel] = useState<string | null>(initialLensId ?? null);
  const fr = lang !== "en";

  useEffect(() => { if (options) setLoaded(options); }, [options]);
  useEffect(() => { if (open) setSel(initialLensId ?? null); }, [open, initialLensId]);
  useEffect(() => {
    if (!open || options || !countryId) return;
    (supabase.from as any)("active_lens_catalog")
      .select("lens_type_id, price_ttc, display_name_fr, display_name_en")
      .eq("country_id", countryId)
      .then(({ data }: any) => setLoaded(((data ?? []) as any[])
        .map((l) => ({ id: l.lens_type_id, display_name_fr: l.display_name_fr, display_name_en: l.display_name_en, price_ttc: Number(l.price_ttc) }))
        .sort((a, b) => a.price_ttc - b.price_ttc)));
  }, [open, options, countryId]);

  const list = loaded ?? [];
  const chosen = list.find((l) => l.id === sel) ?? null;
  const fmt = (n: number) => `${n.toLocaleString(fr ? "fr-FR" : "en-US")} ${currency}`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{fr ? "Choisis tes verres" : "Choose your lenses"}</SheetTitle>
          <SheetDescription>{fr ? "Prix tout compris : monture + verres." : "All-inclusive price: frame + lenses."}</SheetDescription>
        </SheetHeader>
        <div className="grid gap-2 py-4">
          {loaded === null && <p className="text-sm text-muted-foreground">{fr ? "Chargement…" : "Loading…"}</p>}
          {list.map((l) => {
            const active = l.id === sel;
            return (
              <button key={l.id} type="button" aria-pressed={active} onClick={() => setSel(l.id)}
                className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left ${active ? "border-primary bg-primary/5" : "border-border"}`}>
                <span className="text-sm min-w-0 break-words">{fr ? l.display_name_fr : l.display_name_en}</span>
                <span className="text-sm font-semibold text-primary whitespace-nowrap">{fmt(framePriceTtc + l.price_ttc)}</span>
              </button>
            );
          })}
        </div>
        <Button size="lg" className="w-full font-bold" disabled={!chosen}
          onClick={() => { if (chosen) { onConfirm(chosen); onOpenChange(false); } }}>
          <span className="truncate">{confirmLabel ?? (fr ? "Valider" : "Confirm")}{chosen ? ` · ${fmt(framePriceTtc + chosen.price_ttc)}` : ""}</span>
        </Button>
      </SheetContent>
    </Sheet>
  );
}
